import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Coffee, LogIn, LogOut, Play, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReportDownload } from '@/hooks/useReportDownload';
import {
  getClockState,
  getActiveDay,
  getClockStateOn,
  hasMissingClockOut,
  sortEventsByTime,
  useTimeclockStore,
  type ClockState,
} from '@/store/useTimeclockStore';
import { useOvertimeStore } from '@/store/useOvertimeStore';
import { useAuthStore } from '@/store/useAuthStore';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Shared/Tabs';
import { FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as TimeClockDayStatusEnum } from '@/api/generated/models';
import type {
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto,
} from '@/api/generated/models';
import { isoDate } from '@/utils/dateUtils';
import { displayDayMinutes, formatDuration, parseTimeSpan } from '@/utils/timeclockTotals';
import { StampTime } from '@/components/Shared/StampTime';
import TimeClockEditSlideOver from './components/TimeClockEditSlideOver';
import { TimeClockDaysTab } from './components/TimeClockDaysTab';
import { MyTripsTab } from './components/MyTripsTab';
import { TripDayLine } from './components/TripDayLine';
import { DayStatusBadge } from './components/EventBadges';
import { eventBadgeConfig, formatIsoDay as formatDate, formatLastEvent } from './components/timeClockFormat';
import { MonthDownloadPicker } from '@/components/Shared/MonthDownloadPicker';
import { OpenDaysNotice } from '@/components/Dashboard/Employee/OpenDaysNotice';
import { OvertimeAccountCard } from './components/OvertimeAccountCard';
import { OvertimeMonthsTable } from './components/OvertimeMonthsTable';
import { useClockOutGuard } from '@/hooks/useClockOutGuard';
import { useClockInWindow } from '@/hooks/useClockInWindow';
import { ClockStampDialogs } from '@/components/Dashboard/Employee/ClockStampDialogs';
import { TripActions } from '@/components/Dashboard/Employee/TripActions';
import { useClockStamp } from '@/hooks/useClockStamp';
import { useHasActiveVehicles } from '@/hooks/useHasActiveVehicles';
import { useDescribeVehicle } from '@/hooks/useDescribeVehicle';
import { useDescribeWorkLocations } from '@/hooks/useDescribeWorkLocation';

export default function TimeClockPage() {
  const {
    todayEntry,
    openDays,
    currentFilter,
    loading,
    error,
    successMessage,
    fetchToday,
    recordEvent,
    fetchHistory,
    fetchOpenDays,
    clearError,
    clearSuccess,
  } = useTimeclockStore();

  const { isAdmin, employee } = useAuthStore();
  const employeeId = employee?.id ?? '';
  const { t } = useTranslation('timeclock');
  const { t: tReports } = useTranslation('reports');
  const { downloading: reportDownloading, download: downloadReportFn } = useReportDownload();
  const [searchParams, setSearchParams] = useSearchParams();

  // Same non-blocking checklist warning as on the dashboard — both clock-out paths go
  // through it so the reminder cannot be sidestepped by switching pages.
  const todayIso = useMemo(() => isoDate(new Date()), []);
  // Clock-in and clock-out ask for the work location and the vehicle of the day first; the
  // checklist warning still comes before that.
  const clockStamp = useClockStamp();
  const stampWithQuestions = (...args: Parameters<typeof clockStamp.stamp>) =>
    clockStamp.stamp(...args).catch(() => {
      // The store has already surfaced the localized error.
    });
  // A night shift past midnight stays on its own day until it is clocked out (FACF-149).
  const activeDay = getActiveDay(todayEntry, openDays, todayIso);
  const clockOutGuard = useClockOutGuard(todayIso, () => stampWithQuestions('clockOut', activeDay.date));
  const describeVehicle = useDescribeVehicle();
  const describeWorkLocations = useDescribeWorkLocations();
  // Per event of one day in chronological order: "Homeoffice · kein Fahrer" — the stretch's home
  // office and the vehicle chosen at this stamp — or null. One pass per day.
  const describeChoices = (
    orderedDayEvents: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto[],
  ) => {
    const locations = describeWorkLocations(orderedDayEvents);
    return orderedDayEvents.map(
      (evt, i) => [locations[i], describeVehicle(evt)].filter(Boolean).join(' · ') || null,
    );
  };

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [editEntry, setEditEntry] = useState<FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { myBalance: overtimeBalance, fetchMyBalance: fetchMyOvertimeBalance } = useOvertimeStore();

  const clockInWindow = useClockInWindow();
  const clockInAllowed = clockInWindow.allowed;
  // Ticks the running day's work and break time once a minute.
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const checkInterval = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => clearInterval(checkInterval);
  }, []);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchToday(), fetchHistory(currentFilter), fetchMyOvertimeBalance()]);
      setInitialLoadComplete(true);
    };
    load();
  }, [fetchToday, fetchHistory, currentFilter, fetchMyOvertimeBalance]);

  useEffect(() => {
    fetchOpenDays();
  }, [fetchOpenDays]);

  // Tabs "Tage"/"Fahrten" (FAFD-68), only for tenants with vehicles; ?tab=trips opens the trips.
  const hasVehicles = useHasActiveVehicles();
  const tab = searchParams.get('tab') === 'trips' ? 'trips' : 'days';
  const showTab = (next: string) =>
    setSearchParams((params) => (next === 'trips' ? params.set('tab', 'trips') : params.delete('tab'), params), { replace: true });

  // The dashboard links here with ?day=YYYY-MM-DD to fix a forgotten clock-out.
  const requestedDay = searchParams.get('day');
  useEffect(() => {
    if (!requestedDay) return;
    const entry = openDays.find((day) => day.date === requestedDay);
    if (!entry) return;
    setEditEntry(entry);
    setIsEditOpen(true);
    setSearchParams(
      (params) => {
        params.delete('day');
        return params;
      },
      { replace: true },
    );
  }, [requestedDay, openDays, setSearchParams]);

  useEffect(() => {
    if (!successMessage) return;
    const timeout = setTimeout(() => clearSuccess(), 3000);
    return () => clearTimeout(timeout);
  }, [successMessage, clearSuccess]);

  const orderedTodayEvents = useMemo(
    () => sortEventsByTime(activeDay.entry?.events),
    [activeDay.entry?.events],
  );
  const todayChoices = describeChoices(orderedTodayEvents);

  const clockState = getClockStateOn(activeDay.entry, activeDay.date);
  const todayMinutes = displayDayMinutes(activeDay.entry, now);
  // Only a finished day has a final credit; while it runs the live minutes are shown alone.
  const todayCreditedMinutes =
    getClockState(activeDay.entry) === 'out' ? parseTimeSpan(activeDay.entry?.creditedWorkTime) : null;
  const buttonState = getButtonState(clockState);
  const previousDayOpen = hasMissingClockOut(openDays, todayIso);
  const clockInRelevant = buttonState.canClockIn;

  const todayIsConfirmed = activeDay.entry?.status === TimeClockDayStatusEnum.NUMBER_2;
  const todayButtonsDisabled = todayIsConfirmed && !isAdmin;

  const hasError = Boolean(error);

  const openEdit = (entry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto) => {
    setEditEntry(entry);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditEntry(null);
  };

  const canEditEntry = (entry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto) => {
    if (isAdmin) return true;
    return entry.status !== TimeClockDayStatusEnum.NUMBER_2;
  };

  const daysTab = <TimeClockDaysTab describeChoices={describeChoices} canEditEntry={canEditEntry} onEdit={openEdit} />;

  if (loading && !initialLoadComplete) {
    return <Loading fullScreen />;
  }

  if (hasError && !initialLoadComplete) {
    return (
      <EmptyState
        variant="error"
        title={t('errors.loadTitle')}
        description={error ?? t('errors.loadDescription')}
        onRetry={() => {
          clearError();
          fetchToday();
          fetchHistory(currentFilter);
        }}
      />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('subtitle')}
            </p>
          </div>
          <MonthDownloadPicker
            label={tReports('actions.downloadMonthly')}
            monthLabel={tReports('labels.month')}
            downloading={reportDownloading}
            onDownload={(year, month) => downloadReportFn('monthly', { year, month })}
            icon={<FileDown className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          />
        </div>
      </div>

      {successMessage && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 text-[13px]">
          {successMessage}
        </div>
      )}

      <OpenDaysNotice
        openDays={openDays}
        todayIso={todayIso}
        busy={loading}
        onEditDay={openEdit}
        onShowUnconfirmed={currentFilter === 'open' ? undefined : () => fetchHistory('open')}
      />

      {overtimeBalance && <OvertimeAccountCard balance={overtimeBalance} />}

      <Card title={t('today.title')} actions={activeDay.entry ? <DayStatusBadge status={activeDay.entry.status} t={t} /> : undefined}>
        <div className="space-y-4">
          {clockInRelevant && !clockInAllowed && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-[13px] text-amber-800">
              {t('banners.stampingClosed', { start: clockInWindow.start, end: clockInWindow.end })}
            </div>
          )}

          {clockInRelevant && clockInWindow.beforeWorkCount && (
            <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-[13px] text-sky-800">
              {t('banners.earlyClockIn', { time: clockInWindow.workCountStart })}
            </div>
          )}

          {activeDay.isRunningShift && (
            <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-[13px] text-sky-800">
              {t('banners.runningShift', { date: formatDate(activeDay.date) })}
            </div>
          )}

          {todayIsConfirmed && !isAdmin && (
            <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-[13px] text-sky-800">
              {t('banners.dayConfirmed')}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => stampWithQuestions('clockIn')}
              disabled={
                !buttonState.canClockIn ||
                loading ||
                clockStamp.preparing ||
                todayButtonsDisabled ||
                !clockInAllowed ||
                previousDayOpen
              }
              leftIcon={<LogIn className="w-4 h-4" strokeWidth={2} />}
            >
              {t('buttons.clockIn')}
            </Button>
            <Button
              onClick={() => recordEvent('breakStart', activeDay.date)}
              disabled={!buttonState.canBreakStart || loading || todayButtonsDisabled}
              variant="secondary"
              leftIcon={<Coffee className="w-4 h-4" strokeWidth={2} />}
            >
              {t('buttons.breakStart')}
            </Button>
            <Button
              onClick={() => recordEvent('breakEnd', activeDay.date)}
              disabled={!buttonState.canBreakEnd || loading || todayButtonsDisabled}
              variant="secondary"
              leftIcon={<Play className="w-4 h-4" strokeWidth={2} />}
            >
              {t('buttons.breakEnd')}
            </Button>
            <Button
              onClick={clockOutGuard.guardClockOut}
              disabled={
                !buttonState.canClockOut ||
                loading ||
                clockOutGuard.checking ||
                clockStamp.preparing ||
                todayButtonsDisabled
              }
              variant="secondary"
              leftIcon={<LogOut className="w-4 h-4" strokeWidth={2} />}
            >
              {t('buttons.clockOut')}
            </Button>
          </div>

          {hasVehicles && <TripActions clockedIn={!buttonState.canClockIn} />}

          {activeDay.entry ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="px-3 py-2.5 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-[11px] text-slate-500">{t('stats.workTime')}</div>
                <div className="text-[15px] font-semibold text-slate-900 tabular-nums mt-0.5">
                  {formatDuration(todayMinutes.workMinutes)}
                </div>
                {todayCreditedMinutes !== null && todayCreditedMinutes !== todayMinutes.workMinutes && (
                  <div className="text-[11px] text-slate-500 mt-0.5" title={t('stats.creditedHint')}>
                    {t('stats.credited', { time: formatDuration(todayCreditedMinutes) })}
                  </div>
                )}
              </div>
              <div className="px-3 py-2.5 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-[11px] text-slate-500">{t('stats.breakTime')}</div>
                <div className="text-[15px] font-semibold text-slate-900 tabular-nums mt-0.5">
                  {formatDuration(todayMinutes.breakMinutes)}
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-[11px] text-slate-500">{t('stats.lastEvent')}</div>
                <div className="text-[13px] font-medium text-slate-900 mt-0.5">
                  {formatLastEvent(orderedTodayEvents, t)}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-[13px] text-slate-500">{t('today.noEntries')}</div>
          )}

          {orderedTodayEvents.length > 0 && (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {orderedTodayEvents.map((evt, idx) => (
                <li
                  key={evt.timeClockEventId ?? idx}
                  className="flex flex-wrap items-baseline gap-x-2 px-3 py-2 text-[13px]"
                >
                  <span className="text-slate-700">
                    {evt.eventType && eventBadgeConfig[evt.eventType]
                      ? t(eventBadgeConfig[evt.eventType].labelKey)
                      : '–'}
                  </span>
                  <StampTime event={evt} className="font-medium text-slate-900" />
                  {todayChoices[idx] && <span className="text-slate-500">· {todayChoices[idx]}</span>}
                  {evt.isSystemGenerated && (
                    <span className="text-slate-500">· {t('events.automatic')}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {hasVehicles && <TripDayLine day={activeDay.date} onShowTrips={() => showTab('trips')} />}
        </div>
      </Card>

      {hasVehicles ? (
        <Tabs value={tab} onValueChange={showTab}>
          <TabsList>
            <TabsTrigger value="days">{t('tabs.days')}</TabsTrigger>
            <TabsTrigger value="trips">{t('tabs.trips')}</TabsTrigger>
          </TabsList>
          <TabsContent value="days">{daysTab}</TabsContent>
          <TabsContent value="trips">
            <MyTripsTab />
          </TabsContent>
        </Tabs>
      ) : (
        daysTab
      )}

      {overtimeBalance && overtimeBalance.months.length > 0 && (
        <Card title={t('overtime.monthlyTitle')} noPadding>
          <OvertimeMonthsTable months={overtimeBalance.months} />
        </Card>
      )}

      <TimeClockEditSlideOver
        isOpen={isEditOpen}
        onClose={closeEdit}
        entry={editEntry}
        isAdmin={isAdmin}
        employeeId={employeeId}
      />

      <ClockStampDialogs clockStamp={clockStamp} clockOutGuard={clockOutGuard} todayIso={todayIso} busy={loading} />
    </div>
  );
}

function getButtonState(state: ClockState) {
  switch (state) {
    case 'in':
      return { canClockIn: false, canClockOut: true, canBreakStart: true, canBreakEnd: false };
    case 'break':
      return { canClockIn: false, canClockOut: true, canBreakStart: false, canBreakEnd: true };
    case 'idle':
    case 'out':
      return { canClockIn: true, canClockOut: false, canBreakStart: false, canBreakEnd: false };
  }
}
